import { Fragment } from 'react';

import { Box } from '#/components/Box/Box';
import { Hide } from '#/components/Hide/Hide';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { H1 } from '#/components/Text/H1';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ListingTypeLabel } from '#/modules/listing/components/ListingTypeLabel/ListingTypeLabel';
import type { ApiOrg } from '#/modules/listing/orgs/api/types/ApiOrg';
import { getCityStateString } from '#/utils/address/getCityStateString';
import { getSocialNetworkFromLink } from '#/utils/social/getSocialNetworkFromLink';
import { formatToHumanReadableUrl } from '#/utils/string/formatToHumanReadableLink/formatToHumanReadableUrl';

import { ViewOrgPageHeaderSocialLinks } from './ViewOrgPage.styled';
import { ViewOrgPageActionButtons } from './ViewOrgPageActionButtons';
import { ViewOrgPageSocialLinks } from './ViewOrgPageSocialLinks';

type Props = {
  org: ApiOrg;
  isPreview: boolean;
  saved: boolean;
  onSave: () => Promise<void>;
  onUnsave: () => Promise<void>;
};

export function ViewOrgPageHeader({
  org,
  isPreview,
  saved,
  onSave,
  onUnsave,
}: Props) {
  const { i18n } = useI18n();

  const nonSocialLinks = org.links.filter(
    (link) => !getSocialNetworkFromLink(link),
  );

  return (
    <>
      <Box mb="24px">
        <ListingTypeLabel listingType="ORG" orgType={org.orgType} />
      </Box>

      <H1
        styleAs="h2"
        color="mediumContentGrey"
        qaId="listing-name"
        lineHeight={1}
        mb="24px"
      >
        {org.name}
      </H1>
      <Box
        lineHeight={1}
        mb={['12px', null, null, '24px']}
        display="flex"
        alignItems="center"
        gridGap={16}
      >
        {org.address && <div>{getCityStateString(i18n, org.address)}</div>}

        {nonSocialLinks.map((link) => (
          <Fragment key={link}>
            <div>{' | '}</div>
            <StyledNativeLinkWithRef
              href={link}
              target="_blank"
              rel="noopener noreferrer"
            >
              {formatToHumanReadableUrl(link)}
            </StyledNativeLinkWithRef>
          </Fragment>
        ))}
      </Box>
      <ViewOrgPageHeaderSocialLinks>
        <ViewOrgPageSocialLinks
          links={org.links}
          qaId="org-social-links-header"
        />
      </ViewOrgPageHeaderSocialLinks>
      <Hide xs sm md>
        <div>
          <ViewOrgPageActionButtons
            org={org}
            isPreview={isPreview}
            mobile={false}
            saved={saved}
            onSave={onSave}
            onUnsave={onUnsave}
          />
        </div>
      </Hide>
    </>
  );
}
